import { NavLink, useParams } from "react-router";
import { useHtzConfirmationShowQuery } from "@/services/backend/htz/confirmations/api/confirmation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  ConfirmationContextProvider,
  useConfirmationContext,
} from "@/routes/processes/[processId]/htz/confirmations/[confirmationId]/_components/confirmation-context";
import {
  useHtzPositionCatalogShowActiveCatalogQuery,
  useHtzPositionCatalogShowQuery,
} from "@/services/backend/htz/position-catalog/api/position-catalog-api";
import { ConfirmationPdf } from "@/routes/processes/[processId]/htz/confirmations/[confirmationId]/_components/confirmation-pdf";
import { DeleteConfirmationDialog } from "@/routes/processes/[processId]/htz/confirmations/[confirmationId]/_components/delete-confirmation-dialog";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import { useHtzOrderListQuery } from "@/services/backend/htz/orders/api/order-api";
import { useHtzOfferListQuery } from "@/services/backend/htz/offers/api/offer-api";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import { useHtzAirHandlingUnitListQuery } from "@/services/backend/htz/ahu/api/ahu-api";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function ConfirmationRoute() {
  const { confirmationId } = useParams();

  const {
    confirmation,
    catalog,
    airHandlingUnits,
    offers,
    orders,
    isLoading,
    error,
  } = useConfirmationRouteDependencies(confirmationId!);

  const breadcrumbs = confirmation && (
    <HtzBreadcrumbs
      customerId={confirmation.customerId}
      processId={confirmation.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink
            to={`/processes/htz/${confirmation.processId!}/confirmations`}
          >
            {t("Auftragsbestätigungen")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={confirmation.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {!isLoading && !error && confirmation && (
        <PersistenceContextProvider>
          <ConfirmationContextProvider
            confirmation={confirmation}
            catalog={catalog!}
            airHandlingUnits={airHandlingUnits!}
            offers={offers!}
            orders={orders!}
          >
            <ConfirmationPage />
          </ConfirmationContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function useConfirmationRouteDependencies(confirmationId: string) {
  const {
    data: confirmation,
    isLoading: confirmationIsLoading,
    error: confirmationError,
  } = useHtzConfirmationShowQuery({
    id: confirmationId!,
  });

  const {
    data: activeCatalog,
    isLoading: activeCatalogIsLoading,
    error: activeCatalogError,
  } = useHtzPositionCatalogShowActiveCatalogQuery(
    {
      companyId: confirmation?.companyId ?? "",
    },
    {
      skip: confirmation === undefined,
    },
  );

  const {
    data: positionCatalog,
    isLoading: catalogIsLoading,
    error: catalogError,
  } = useHtzPositionCatalogShowQuery(
    {
      id: activeCatalog?.catalogId ?? "",
    },
    {
      skip: activeCatalog === undefined || activeCatalog?.catalogId === null,
    },
  );

  const {
    data: ahuList,
    isLoading: ahuIsLoading,
    error: ahuError,
  } = useHtzAirHandlingUnitListQuery(
    {
      customer: {
        active: confirmation !== undefined,
        values: [confirmation?.customerId ?? ""],
      },
    },
    {
      skip: confirmation === undefined,
    },
  );
  const airHandlingUnits = ahuList?.items ?? [];

  const {
    data: orderList,
    isLoading: orderIsLoading,
    error: orderError,
  } = useHtzOrderListQuery(
    {
      process: {
        active: true,
        values: [confirmation?.processId ?? ""],
      },
    },
    {
      skip: confirmation === undefined,
    },
  );
  const orders = orderList?.items ?? [];

  const {
    data: offerList,
    isLoading: offerIsLoading,
    error: offerError,
  } = useHtzOfferListQuery(
    {
      process: {
        active: true,
        values: [confirmation?.processId ?? ""],
      },
      immutable: {
        active: true,
        values: [true],
      },
    },
    {
      skip: confirmation === undefined,
    },
  );
  const offers = offerList?.items ?? [];

  const isLoading =
    confirmationIsLoading ||
    activeCatalogIsLoading ||
    catalogIsLoading ||
    ahuIsLoading ||
    orderIsLoading ||
    offerIsLoading;

  const error = (() => {
    switch (true) {
      case confirmationError !== undefined:
        return confirmationError;
      case activeCatalogError !== undefined:
        return activeCatalogError;
      case catalogError !== undefined:
        return catalogError;
      case ahuError !== undefined:
        return ahuError;
      case orderError !== undefined:
        return orderError;
      case offerError !== undefined:
        return offerError;
      case activeCatalog?.catalogId === null:
        // fake 404 error
        return {
          status: 404,
        };
      default:
        return undefined;
    }
  })();

  return {
    confirmation,
    catalog: positionCatalog,
    airHandlingUnits,
    offers,
    orders,
    isLoading,
    error,
  };
}

function ConfirmationPage() {
  const { confirmation, validationError } = useConfirmationContext();

  const hasDataError = hasFieldError(validationError, "date");
  const hasPosError = hasFieldError(validationError, "positions");
  const hasAddrError = hasFieldError(
    validationError,
    "postalAddress",
    "serviceAddress",
  );
  const hasRepError = hasFieldError(validationError, "representative");

  const links = [
    {
      label: t("Auftragsbestätugung"),
      to: "data",
      hasValidationError: hasDataError,
    },
    {
      label: t("Leistungen"),
      to: "positions",
      hasValidationError: hasPosError,
    },
    {
      label: t("Adressen & Kontakte"),
      to: "addresses-and-contacts",
      hasValidationError: hasAddrError || hasRepError,
    },
  ];

  return (
    <DocumentPage>
      <DocumentImmutableAlert document={confirmation} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              hasValidationError={link.hasValidationError}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!confirmation.immutable && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZConfirmation}
                destinationId={confirmation.id}
                initialParams={{
                  // We limit the results to documents
                  // from the same process. This
                  // is the 90% default what users
                  // need. If needed, they can change
                  // the filter.
                  process: {
                    active: confirmation.processId !== null,
                    values: [confirmation.processId || NullUUID],
                  },
                  orderBy: [{ field: "updatedAt", dsc: true }],
                }}
              />
              <DeleteConfirmationDialog confirmationId={confirmation.id} />
            </>
          )}
          <ConfirmationPdf confirmation={confirmation} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
